import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Users, Home, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { useFreemium } from "@/hooks/useFreemium";
import MemberFormDialog from "@/components/MemberFormDialog";
import FamilyFormDialog from "@/components/FamilyFormDialog";
import Members from "./Members";
import Families from "./Families";
import MissingInfoPage from "./MissingInfoPage";

type PeopleTab = "members" | "families" | "missing";

const getValidTab = (value: string | null): PeopleTab => {
  if (value === "families") return "families";
  if (value === "missing") return "missing";
  return "members";
};

const People = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getValidTab(searchParams.get("tab"));
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddFamily, setShowAddFamily] = useState(false);
  const { isAtMemberLimit } = useSubscription();
  const { requireSubscription } = useFreemium();

  const { data: members = [] } = useQuery({
    queryKey: ["people_export_members"],
    queryFn: async () => {
      const { data, error } = await supabase.from("members").select("full_name, email, phone, department, status, gender, baptism_status, date_joined, date_of_birth, city, state").order("full_name");
      if (error) throw error;
      return data;
    },
  });

  const { data: families = [] } = useQuery({
    queryKey: ["families_export"],
    queryFn: async () => {
      const { data: fams, error: fErr } = await supabase
        .from("families")
        .select("id, family_name, address, city, state, zip_code, phone, notes")
        .order("family_name");
      if (fErr) throw fErr;

      const { data: links, error: lErr } = await supabase
        .from("family_members")
        .select("family_id, member_id");
      if (lErr) throw lErr;

      const memberCountByFamily: Record<string, number> = {};
      (links || []).forEach((link: any) => {
        memberCountByFamily[link.family_id] = (memberCountByFamily[link.family_id] || 0) + 1;
      });

      return (fams || []).map((family: any) => ({
        ...family,
        members_count: memberCountByFamily[family.id] || 0,
      }));
    },
  });

  const handleTabChange = (nextTab: string) => {
    const tab = getValidTab(nextTab);
    setSearchParams({ tab }, { replace: true });
  };

  const exportMembersCSV = () => {
    const headers = ["Full Name", "Email", "Phone", "Ministry", "Status", "Gender", "Baptized", "Date Joined", "Birthday", "City", "State"];
    const rows = members.map((member: any) => [
      member.full_name,
      member.email || "",
      member.phone || "",
      member.department || "",
      member.status || "",
      member.gender || "",
      member.baptism_status ? "Yes" : "No",
      member.date_joined || "",
      member.date_of_birth || "",
      member.city || "",
      member.state || "",
    ]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "members.csv";
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success("Members exported!");
  };

  const exportFamiliesCSV = () => {
    const headers = ["Family Name", "Address", "City", "State", "ZIP Code", "Phone", "Members", "Notes"];
    const rows = families.map((family: any) => [
      family.family_name,
      family.address || "",
      family.city || "",
      family.state || "",
      family.zip_code || "",
      family.phone || "",
      family.members_count || 0,
      family.notes || "",
    ]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "families.csv";
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success("Families exported!");
  };

  const handleExport = () => {
    if (activeTab === "families") {
      exportFamiliesCSV();
      return;
    }
    exportMembersCSV();
  };

  const handleAdd = () => {
    if (!requireSubscription()) return;
    if (activeTab === "families") {
      setShowAddFamily(true);
      return;
    }
    setShowAddMember(true);
  };

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <div className="flex items-center gap-2">
          <TabsList className="h-9 rounded-none bg-muted p-1 gap-1">
          <TabsTrigger
            value="members"
            className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <Users className="w-3.5 h-3.5 mr-1.5" />Members
          </TabsTrigger>
          <TabsTrigger
            value="families"
            className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <Home className="w-3.5 h-3.5 mr-1.5" />Families
          </TabsTrigger>
          <TabsTrigger
            value="missing"
            className="h-7 px-3 text-xs font-medium rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <AlertCircle className="w-3.5 h-3.5 mr-1.5" />Missing Info
          </TabsTrigger>
        </TabsList>
      </div>

        <TabsContent value="members" className="space-y-0">
          <Members hideTitle hideActions onAdd={activeTab === "members" ? handleAdd : undefined} />
        </TabsContent>

        <TabsContent value="families" className="space-y-0">
          <Families hideTitle hideActions onAdd={activeTab === "families" ? handleAdd : undefined} />
        </TabsContent>

        <TabsContent value="missing" className="space-y-0">
          <MissingInfoPage hideTitle hideBackButton />
        </TabsContent>
      </Tabs>

      <MemberFormDialog open={showAddMember} onClose={() => setShowAddMember(false)} member={null} />
      <FamilyFormDialog open={showAddFamily} onClose={() => setShowAddFamily(false)} family={null} />
    </div>
  );
};

export default People;
